// import React from 'react';
// import { List, Typography, Divider, Card } from 'antd';
// import './HistoryDisplay.css'; // Import the CSS file
// import { CloseCircleFilled, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons'

// const { Text, Title } = Typography;

// /**
//  * Renders a display of history data using Ant Design List component.
//  * @param {Array<Object>} historyData - The array of history objects to display.
//  */
// const HistoryDisplay = ({ screendata, historyData=[], publishContentLogData=[]}) => {
//   // Check if data is provided and is an array
//   if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
//     return (
//       <Card title="History Log">
//         <Text type="secondary">No history data available.</Text>
//       </Card>
//     );
//   }

//   // Optional: Reverse the data to show the newest entries first, which is typical for history/log displays.
//   const displayData = [...historyData].reverse();

//   return (
//     <Card 
//       title={<Title level={4} style={{ margin: 0 }}>{`Publishing History of "${screendata.ScreenName}"`}</Title>} 
//       className="history-card"
//     >
//       <List
//         className="history-list"
//         itemLayout="horizontal"
//         dataSource={displayData}
//         renderItem={item => (
//           <List.Item className="history-list-item">
//             <List.Item.Meta
//               // The primary detail/action performed
//               title={
//                 <div className="detail-title">
//                   <Text strong>{item.Detail}</Text>
//                 </div>
//               }
//               // Secondary information: Screen ID, Brand ID, and Timestamp
//               description={
//                 <div className="item-description">
//                   <Text type="secondary" className="meta-text">
//                     Screen ID: **{item.ScreenID}** | Brand ID: **{item.BrandID}**
//                   </Text>
//                   <Text className="timestamp-text">
//                     <span role="img" aria-label="clock"><ClockCircleOutlined/></span> {item.AddDate}
//                   </Text>
//                 </div>
//               }
//             />
//           </List.Item>
//         )}
//       />
//       <Divider />
//       <Text type="secondary" style={{ float: 'right' }}>Total Entries: {historyData.length}</Text>
//     </Card>
//   );
// };

// export default HistoryDisplay;
import React from 'react';
import { List, Typography, Card, Space, Tag, Divider, Collapse } from 'antd'; // Imported Collapse
import './HistoryDisplay.css';
import { ClockCircleOutlined, PushpinOutlined, FolderOpenOutlined, FileDoneOutlined, IssuesCloseOutlined } from '@ant-design/icons'
import { THEME_PALLETE } from '../../Theme';

const { Text, Title } = Typography;
const { Panel } = Collapse; // Destructure Panel

/**
 * Parses date strings into Date objects for comparison.
 * Assumes the format "YYYY-MM-DD HH:mm:ss".
 * @param {string} dateString
 * @returns {Date}
 */
const parseDate = (dateString) => {
    // Replace space with 'T' to create ISO format for reliable Date parsing
    return new Date(dateString.replace(' ', 'T'));
};

/**
 * Groups historyData under the nearest preceding or current publishContentLogData event.
 * @param {Array<Object>} historyData - Array of history objects.
 * @param {Array<Object>} publishContentLogData - Array of publish log objects.
 * @returns {Array<Object>} Structured data for display.
 */
const groupHistoryByPublishEvent = (historyData, publishContentLogData) => {
    // 1. Sort both lists by AddDate in ascending order for grouping logic
    const sortedHistory = [...historyData].sort((a, b) => parseDate(a.AddDate) - parseDate(b.AddDate));
    const sortedPublishLogs = [...publishContentLogData].sort((a, b) => parseDate(a.AddDate) - parseDate(b.AddDate));

    // Initialize the structure: publish logs with an empty historyItems array
    const groupedData = sortedPublishLogs.map(log => ({
        ...log,
        // Add a unique key for Collapse panel and easier date comparison
        key: log.AddDate,
        publishDate: parseDate(log.AddDate),
        historyItems: [],
    }));

    // Add a synthetic "Initial/Unpublished" group for history before the first publish event
    if (groupedData.length > 0) {
        // Create a synthetic "Pre-Publish" log for items before the first official publish event
        groupedData.unshift({
            Detail: 'Initial Content/Pre-First Publish Logs',
            AddDate: 'N/A',
            key: 'pre-publish',
            publishDate: new Date(0), // Earliest possible date
            historyItems: [],
        });
    }

    // 2. Group history items
    for (const historyItem of sortedHistory) {
        const historyDate = parseDate(historyItem.AddDate);

        // Find the LATEST publish log whose date is <= historyDate
        let bestMatchIndex = -1;

        for (let i = groupedData.length - 1; i >= 0; i--) {
            if (groupedData[i].publishDate >= historyDate) {
                bestMatchIndex = i;
            } else {
                break;
            }
        }

        if (bestMatchIndex !== -1) {
            groupedData[bestMatchIndex].historyItems.push(historyItem);
        }
    }

    // Reverse the order of the top-level groups to show the newest publish event first
    const finalGroupedData = groupedData.reverse().filter(group => group.historyItems.length > 0);

    // Also reverse the history items within each group to show the newest history first
    finalGroupedData.forEach(group => group.historyItems.reverse());

    return finalGroupedData;
};

/**
 * Renders a display of history data grouped by publishing events using Collapse.
 * @param {Object} props
 * @param {Object} props.screendata - Data about the screen (e.g., ScreenName).
 * @param {Array<Object>} props.historyData - The array of history objects to display.
 * @param {Array<Object>} props.publishContentLogData - The array of publish log objects.
 */
const HistoryDisplay = ({ screendata, historyData = [], publishContentLogData = [] }) => {
    if (!historyData || historyData.length === 0) {
        return (
            <Card title="History Log">
                <Text type="secondary">No history data available.</Text>
            </Card>
        );
    }

    const groupedHistory = groupHistoryByPublishEvent(historyData, publishContentLogData);
    // Create an array of keys for the newest group to be open by default
    const defaultActiveKey = groupedHistory.length > 0 ? [groupedHistory[0].key] : [];

    // Function to render the custom header for each collapse panel
    const renderPanelHeader = (group) => (
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space align="center">
                <PushpinOutlined style={{ color: THEME_PALLETE.darkgray }} />
                <Text strong style={{ color: THEME_PALLETE.darkgray }}>
                    {group.Detail === 'Initial Content/Pre-First Publish Logs' ? (
                        <Text type="secondary" italic>
                            {group.Detail}
                        </Text>
                    ) : (
                        <>
                            Published on {group.AddDate}
                        </>
                    )}
                </Text>
            </Space>
            <Tag color="blue">
                <IssuesCloseOutlined style={{ marginRight: 4 }} />
                {group.historyItems.length} Change{group.historyItems.length !== 1 ? 's' : ''}
            </Tag>
        </Space>
    );

    return (
        <Card
            title={<Title level={4} style={{ margin: 0 }}>{`Publishing History of "${screendata.ScreenName}"`}</Title>}
            className="history-card"
        >
            <Collapse
                defaultActiveKey={defaultActiveKey} // Open the newest event by default
                expandIconPosition="right"
                style={{ width: '100%' }}
            >
                {groupedHistory.map((group) => (
                    <Panel
                        header={renderPanelHeader(group)}
                        key={group.key} // Use the unique key generated in grouping
                        className="publish-collapse-panel"
                    >
                        <List
                            className="history-list grouped-list"
                            itemLayout="horizontal"
                            dataSource={group.historyItems}
                            // The list is now rendered inside the collapsed panel
                            renderItem={item => (
                                <List.Item className="history-list-item" style={{ paddingLeft: 0 }}>
                                    <List.Item.Meta
                                        title={
                                            <div className="detail-title">
                                                <Text strong>{item.Detail}</Text>
                                            </div>
                                        }

                                    />
                                    <Text className="timestamp-text">
                                        <ClockCircleOutlined /> {item.AddDate}
                                    </Text>
                                </List.Item>
                            )}
                        />
                    </Panel>
                ))}
            </Collapse>

            <Divider style={{ margin: '16px 0' }} />
            <Text type="secondary" style={{ float: 'right' }}>Total Entries: {groupedHistory.length}</Text>
        </Card>
    );
};

export default HistoryDisplay;